/**
 * DeleteNetworkDialog — gated, multi-state confirmation for the
 * destructive ``DELETE /networks/{id}`` flow.
 *
 * Mirrors the pattern of ``RestoreConfirmDialog``:
 *
 *   1. On open, fetch the impact preview (``getNetworkDeletionImpact``).
 *   2. If the preview comes back with ``canDelete === false``, render the
 *      BLOCKED state — list active deployments + pinned pools, link the
 *      user to the Snapshots tab to deactivate, no destructive form.
 *   3. Otherwise render the DELETABLE state — the heavy "this will
 *      delete X, Y, and Z" copy, the list of user snapshots that will
 *      be wiped (excluding the auto-managed baseline), and the
 *      type-the-name confirmation input.
 *   4. On submit, call ``deleteNetwork`` with the typed token. Server
 *      enforces the same gates again — a racing release will surface
 *      as a ``409 NETWORK_HAS_ACTIVE_RELEASES`` and we re-render the
 *      BLOCKED state without losing the user's place.
 *
 * All decision logic (mode resolution, button enable, copy generation)
 * lives in ``utils/networkDelete.js`` so this component stays a
 * presenter and Vitest can lock the rules without spinning up a DOM.
 */
import React, { useEffect, useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { displayVersionLabel } from '@stepflow/ui-shared/utils/stepflowVersionLabel';
import {
  deleteNetwork,
  getNetworkDeletionImpact,
} from '@/services/networkRestoreApi';
import {
  canSubmitDelete,
  confirmMatchesNetworkName,
  describeBlockingReleases,
  describeDeletionImpact,
  describeDeleteOutcome,
  deletesRightAway,
  DELETE_MODE_BLOCKED,
  DELETE_MODE_DELETABLE,
  DELETE_MODE_LOADING,
  listBlockingReleases,
  listDeletionSnapshots,
  resolveDeleteMode,
} from '@/utils/networkDelete';

export default function DeleteNetworkDialog({
  open,
  networkId,
  networkName,
  onClose,
  onDeleted,
}) {
  const [impact, setImpact] = useState(null);
  const [loading, setLoading] = useState(false);
  const [loadError, setLoadError] = useState(null);
  const [submitting, setSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState(null);
  const [typedConfirm, setTypedConfirm] = useState('');

  // Impact fetch — fires every time the dialog opens for a (possibly
  // different) network. We refetch on networkId change so reusing the
  // same instance for two networks back-to-back can't show stale impact.
  useEffect(() => {
    if (!open || !networkId) return undefined;
    let cancelled = false;
    setImpact(null);
    setLoadError(null);
    setSubmitError(null);
    setTypedConfirm('');
    setLoading(true);
    getNetworkDeletionImpact(networkId)
      .then((data) => {
        if (cancelled) return;
        setImpact(data);
      })
      .catch((e) => {
        if (cancelled) return;
        // Fail closed: a missing impact resolves to BLOCKED so the
        // destructive form never shows when we don't know the state.
        setLoadError(e?.message || 'Failed to load deletion impact');
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [open, networkId]);

  const mode = resolveDeleteMode({ impact, loading });
  const canSubmit = canSubmitDelete({
    impact,
    networkName,
    typedConfirm,
    loading,
    submitting,
  });
  const confirmOk = confirmMatchesNetworkName(typedConfirm, networkName);

  const handleSubmit = async (e) => {
    e?.preventDefault?.();
    if (!canSubmit) return;
    setSubmitting(true);
    setSubmitError(null);
    try {
      const res = await deleteNetwork(networkId, {
        confirm: typedConfirm.trim(),
      });
      onDeleted?.(res);
    } catch (err) {
      const message = err?.message || 'Delete failed';
      // the server's own words (THE TRASH policy: rooms not archived yet, …) — never the raw JSON
      const rooms = (err?.data?.rooms || []).map((r) => r.name || r.poolId).join(', ');
      setSubmitError(err?.data?.message ? `${err.data.message}${rooms ? ` (${rooms})` : ''}` : message);
      // If a release landed between dialog open and submit, the server
      // returns ``409 NETWORK_HAS_ACTIVE_RELEASES`` — refetch impact so
      // we flip to the BLOCKED state instead of stranding the user on
      // a broken type-to-confirm form.
      if (/NETWORK_HAS_ACTIVE_RELEASES/i.test(message)) {
        try {
          const fresh = await getNetworkDeletionImpact(networkId);
          setImpact(fresh);
          setTypedConfirm('');
        } catch {
          // swallow — original error is already surfaced
        }
      }
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <AnimatePresence>
      {open && (
        <motion.div
          className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4"
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          aria-modal="true"
          role="dialog"
        >
          <motion.div
            className="w-full max-w-lg bg-white rounded-xl shadow-xl border border-gray-200 overflow-hidden"
            initial={{ scale: 0.98, y: 6 }}
            animate={{ scale: 1, y: 0 }}
            exit={{ scale: 0.98, y: 6 }}
          >
            <header className="px-5 py-4 border-b border-gray-100 flex items-start justify-between gap-3">
              <div>
                <h3 className="text-base font-semibold text-gray-900">
                  Delete network
                </h3>
                <p className="text-xs text-gray-600 mt-1">
                  <span className="font-medium text-gray-700">{networkName}</span>
                </p>
              </div>
              <button
                type="button"
                onClick={onClose}
                className="text-gray-400 hover:text-gray-700 text-sm"
                aria-label="Close delete dialog"
              >
                ✕
              </button>
            </header>

            <form onSubmit={handleSubmit} className="px-5 py-4 space-y-4">
              {mode === DELETE_MODE_LOADING && (
                <p className="text-sm text-gray-500">
                  Checking deployment impact…
                </p>
              )}

              {loadError && (
                <p className="text-sm text-red-700 bg-red-50 border border-red-100 rounded-lg px-3 py-2">
                  {loadError}
                </p>
              )}

              {mode === DELETE_MODE_BLOCKED && !loading && !loadError && (
                <BlockedState impact={impact} />
              )}

              {mode === DELETE_MODE_DELETABLE && (
                <DeletableState
                  impact={impact}
                  networkName={networkName}
                  typedConfirm={typedConfirm}
                  confirmOk={confirmOk}
                  onChangeTypedConfirm={(v) => {
                    setTypedConfirm(v);
                    setSubmitError(null);
                  }}
                />
              )}

              {submitError && (
                <p className="text-sm text-red-700 bg-red-50 border border-red-100 rounded-lg px-3 py-2">
                  {submitError}
                </p>
              )}

              <div className="flex items-center justify-end gap-2 pt-2">
                <button
                  type="button"
                  onClick={onClose}
                  className="text-sm px-3 py-1.5 rounded-lg border border-gray-300 text-gray-800 hover:bg-gray-50"
                  disabled={submitting}
                >
                  {mode === DELETE_MODE_BLOCKED ? 'Close' : 'Cancel'}
                </button>
                {mode === DELETE_MODE_DELETABLE && (
                  <button
                    type="submit"
                    disabled={!canSubmit}
                    className="text-sm px-3 py-1.5 rounded-lg bg-red-600 text-white hover:bg-red-700 disabled:opacity-40 disabled:cursor-not-allowed"
                    aria-label={deletesRightAway(impact) ? 'Delete network permanently' : 'Move network to the Trash'}
                  >
                    {submitting ? 'Deleting…' : 'Delete network'}
                  </button>
                )}
              </div>
            </form>
          </motion.div>
        </motion.div>
      )}
    </AnimatePresence>
  );
}

function BlockedState({ impact }) {
  const releases = listBlockingReleases(impact);
  const summary = describeBlockingReleases(impact);
  // THE TRASH (LIFECYCLE.md §3b) — a protected workspace deletes a network only after its rooms are archived
  if (impact?.blockReason === 'NETWORK_HAS_LIVE_ROOMS') {
    const rooms = impact.liveRooms || [];
    return (
      <div className="space-y-3" data-testid="delete-blocked-rooms">
        <div className="rounded-lg border border-amber-200 bg-amber-50 px-3 py-2 text-sm text-amber-900">
          <p className="font-medium">{rooms.length} room{rooms.length === 1 ? '' : 's'} still hold work.</p>
          <p className="mt-1 text-xs">
            This workspace protects its work: a network is deleted only after each of its rooms is Retired, then
            Archived (the room&apos;s settings → Lifecycle). The workspace owner can change this in Workspace settings →
            Tenant.
          </p>
        </div>
        <ul className="divide-y divide-gray-100 rounded-lg border border-gray-200 text-xs">
          {rooms.map((r) => (
            <li key={r.poolId} className="flex items-center justify-between gap-3 px-3 py-2 text-gray-800">
              <span>{r.name || r.poolId}</span>
              <span className="text-gray-500">{String(r.status || '').toLowerCase()}</span>
            </li>
          ))}
        </ul>
      </div>
    );
  }
  return (
    <div className="space-y-3">
      <div className="rounded-lg border border-amber-200 bg-amber-50 px-3 py-2 text-sm text-amber-900">
        <p className="font-medium">This network is still deployed.</p>
        <p className="mt-1 text-xs">
          Deactivate every release from the Snapshots tab before deleting
          the network. Deactivating freezes the released version so pinned
          pools keep working until you're ready to retire them.
        </p>
        {summary && (
          <p className="mt-2 text-xs font-medium">{summary}</p>
        )}
      </div>

      {releases.length > 0 && (
        <div className="rounded-lg border border-gray-200">
          <p className="px-3 py-2 text-[11px] uppercase tracking-wide font-semibold text-gray-600 border-b border-gray-100">
            Active releases
          </p>
          <ul className="divide-y divide-gray-100">
            {releases.map((r) => (
              <li
                key={r.releaseVersion}
                className="px-3 py-2 text-xs text-gray-800 flex items-center justify-between gap-3"
              >
                <span className="font-mono">{displayVersionLabel(r, releases) || `Release ${r.releaseVersion}`}</span>
                <span className="text-gray-500">
                  {r.pinnedPoolCount === 0
                    ? 'no pinned pools'
                    : `${r.pinnedPoolCount} pinned pool${
                        r.pinnedPoolCount === 1 ? '' : 's'
                      }`}
                </span>
              </li>
            ))}
          </ul>
        </div>
      )}
    </div>
  );
}

function DeletableState({
  impact,
  networkName,
  typedConfirm,
  confirmOk,
  onChangeTypedConfirm,
}) {
  const snapshots = listDeletionSnapshots(impact);
  return (
    <>
      <div className="rounded-lg border border-red-200 bg-red-50 px-3 py-2 text-sm text-red-900" data-testid="delete-outcome">
        {describeDeleteOutcome(impact)}
        {Number(impact?.activeReleaseCount || 0) > 0 ? (
          <span className="mt-1 block text-xs" data-testid="delete-live-releases">
            It is still released ({impact.activeReleaseCount} live release{impact.activeReleaseCount === 1 ? '' : 's'}) —
            its rooms stop running with it.
          </span>
        ) : null}
      </div>

      {snapshots.length > 0 && (
        <div className="rounded-lg border border-gray-200">
          <p className="px-3 py-2 text-[11px] uppercase tracking-wide font-semibold text-gray-600 border-b border-gray-100">
            {deletesRightAway(impact) ? 'Saved snapshots that will also be deleted'
              : 'Saved snapshots that go with it when it is removed for good'}
            <span className="ml-1 font-normal normal-case text-gray-400">
              (golden baseline excluded from this list)
            </span>
          </p>
          <ul className="divide-y divide-gray-100 max-h-44 overflow-y-auto">
            {snapshots.map((s) => (
              <li
                key={s.capturedAt}
                className="px-3 py-2 text-xs text-gray-800 flex items-center justify-between gap-3"
              >
                <span className="font-mono text-gray-600">
                  {s.capturedAt.replace('T', ' ').slice(0, 16)}
                </span>
                <span className="truncate text-gray-700">
                  {s.label || '(unlabeled)'}
                </span>
              </li>
            ))}
          </ul>
        </div>
      )}

      <div className="space-y-1">
        <label className="text-xs font-medium text-gray-700 flex flex-col gap-1">
          Type{' '}
          <span className="font-mono text-[11px] bg-gray-100 rounded px-1 py-0.5">
            {networkName}
          </span>{' '}
          to confirm
          <input
            type="text"
            value={typedConfirm}
            onChange={(e) => onChangeTypedConfirm(e.target.value)}
            className={`mt-1 text-sm border rounded-lg px-3 py-2 ${
              confirmOk
                ? 'border-emerald-300 focus:ring-emerald-400 focus:border-emerald-400'
                : 'border-gray-300 focus:ring-blue-400 focus:border-blue-400'
            } outline-none focus:ring-2`}
            autoComplete="off"
            spellCheck={false}
            placeholder={networkName}
            aria-label="Type network name to confirm delete"
          />
        </label>
      </div>
    </>
  );
}
